import * as React from 'react';
import { Slideover, SlideoverContent } from './slideover';
import { GlobalDetailPanelSkeleton } from './global-detail-panel-skeleton';
import { useGlobalDetailPanelContext } from '@/components/providers/GlobalDetailPanelProvider';
import type { StackedPanel } from '@/store/panel-stack-store';

// Animation duration - must match Slideover's animation duration
const ANIMATION_DURATION = 300;

const SequenceDetailContent = React.lazy(() =>
  import('@/features/sequences/components/detail/SequenceDetailContentV2').then((mod) => ({
    default: mod.SequenceDetailContentV2,
  })),
);

interface PanelWithState extends StackedPanel {
  isOpen: boolean;
  originalIndex: number;
}

interface GlobalPanelItemProps {
  panel: PanelWithState;
  onClose: () => void;
  onNavigate: (newId: string) => void;
  onAnimationEnd: () => void;
}

function GlobalPanelItem({ panel, onClose, onNavigate, onAnimationEnd }: GlobalPanelItemProps) {
  const { id, type, isOpen, originalIndex } = panel;

  // When the panel closes, signal the end of the exit animation after its duration
  React.useEffect(() => {
    if (isOpen) return;
    const timer = setTimeout(onAnimationEnd, ANIMATION_DURATION);
    return () => clearTimeout(timer);
  }, [isOpen, onAnimationEnd]);

  return (
    <Slideover
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      level={originalIndex}
      disableBodyLock={originalIndex > 0}
      disableBackdrop={originalIndex > 0}
    >
      <SlideoverContent className="p-0 h-full overflow-hidden">
        {type === 'sequence' ? (
          <React.Suspense fallback={<GlobalDetailPanelSkeleton variant="standard" />}>
            <SequenceDetailContent sequenceId={id} isInPanel onNavigate={onNavigate} />
          </React.Suspense>
        ) : (
          <div className="p-6">Unknown entity type</div>
        )}
      </SlideoverContent>
    </Slideover>
  );
}

/**
 * Renders the global detail panel stack (sequence panels only) with exit animations.
 * Place it inside GlobalDetailPanelProvider at the app root.
 */
export function GlobalDetailPanelRenderer() {
  const { stack, closePanelAt, navigateWithinPanel } = useGlobalDetailPanelContext();

  // Panels that are animating out, keyed by panelId
  const closingPanelsRef = React.useRef<Map<string, PanelWithState>>(new Map());
  const prevStackRef = React.useRef<StackedPanel[]>([]);
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);

  // Detect removed panels synchronously during render so the exit animation can play
  const currentPanelIds = new Set(stack.map((p) => p.panelId));
  prevStackRef.current.forEach((panel, index) => {
    if (!currentPanelIds.has(panel.panelId) && !closingPanelsRef.current.has(panel.panelId)) {
      closingPanelsRef.current.set(panel.panelId, {
        ...panel,
        isOpen: false,
        originalIndex: index,
      });
    }
  });
  prevStackRef.current = stack;

  const handleAnimationEnd = React.useCallback((key: string) => {
    closingPanelsRef.current.delete(key);
    forceUpdate();
  }, []);

  // Escape closes the topmost panel
  React.useEffect(() => {
    if (stack.length === 0) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closePanelAt(stack.length - 1);
      }
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [stack.length, closePanelAt]);

  const closingPanels = Array.from(closingPanelsRef.current.values());
  if (stack.length === 0 && closingPanels.length === 0) return null;

  // Closing panels take precedence over active ones with the same panelId
  const panelsToRender = new Map<string, PanelWithState>();
  stack.forEach((panel, index) => {
    panelsToRender.set(panel.panelId, { ...panel, isOpen: true, originalIndex: index });
  });
  closingPanels.forEach((panel) => panelsToRender.set(panel.panelId, panel));

  return (
    <>
      {Array.from(panelsToRender.entries()).map(([key, panel]) => (
        <GlobalPanelItem
          key={key}
          panel={panel}
          onClose={() => {
            if (panel.isOpen) closePanelAt(panel.originalIndex);
          }}
          onNavigate={(newId) => {
            if (panel.isOpen) navigateWithinPanel(panel.originalIndex, newId);
          }}
          onAnimationEnd={() => {
            if (!panel.isOpen) handleAnimationEnd(key);
          }}
        />
      ))}
    </>
  );
}
